<template>
  <section class="batch-modify">
    <header class="batch-head">
      <div class="crumb">
        <button class="plain" @click="$emit('back')">‹ 返回</button>
        <span>
          商品工作台　›　批量修改　›
          <strong>删除商品</strong>
        </span>
      </div>
      <h1>批量删除商品</h1>
      <p class="hint">将删除所选抖店商品，恢复能力以抖店平台规则为准，请谨慎操作。</p>
      <nav>
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="{ active: mode === tab.value }"
          :disabled="confirming"
          @click="mode = tab.value"
        >
          {{ tab.label }}
        </button>
      </nav>
    </header>
    <main>
      <section class="panel">
        <div v-if="mode === 'products'" class="picked">
          <button @click="openSelector">选择商品</button>
          <span>
            已选择
            <b>{{ selected.length }}</b>
            个商品
          </span>
        </div>

        <div v-if="mode === 'product_ids'" class="ids">
          <label>抖店商品 ID</label>
          <el-input
            v-model="idsText"
            type="textarea"
            :rows="3"
            placeholder="多个 ID 用逗号、空格或换行分隔，支持 Excel 粘贴"
          />
        </div>

        <div v-if="mode === 'range'" class="filters">
          <label v-for="field in shownFields" :key="field.key" class="field">
            <span>
              {{ field.label }}
              <small v-if="field.runtime">（需核验数据）</small>
            </span>
            <doudian-category-cascader
              v-if="field.key === 'category_id'"
              v-model="filters.category_id"
              :categories="categories"
            />
            <el-date-picker
              v-else-if="field.date"
              v-model="filters[field.key]"
              type="datetimerange"
              value-format="yyyy-MM-dd HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
            />
            <div v-else-if="field.matchKey" class="combined-control">
              <el-select v-model="filters[field.matchKey]" :aria-label="field.label + '匹配方式'">
                <el-option
                  v-for="option in field.matchOptions"
                  :key="option.value"
                  :value="option.value"
                  :label="option.label"
                />
              </el-select>
              <div v-if="field.range" class="range">
                <input v-model="filters[field.key + '_min']" placeholder="最小值" />
                <span>~</span>
                <input v-model="filters[field.key + '_max']" placeholder="最大值" />
              </div>
              <input
                v-else
                v-model="filters[field.key]"
                :aria-label="field.label"
                :disabled="filters[field.matchKey] === 'empty'"
                :placeholder="field.placeholder || '请输入' + field.label"
              />
            </div>
            <div v-else-if="field.range" class="range">
              <input v-model="filters[field.key + '_min']" placeholder="最小值" />
              <span>~</span>
              <input v-model="filters[field.key + '_max']" placeholder="最大值" />
            </div>
            <el-select v-else-if="field.options" v-model="filters[field.key]" clearable>
              <el-option
                v-for="option in field.options"
                :key="String(option.value)"
                :value="option.value"
                :label="option.label"
              />
            </el-select>
            <input v-else v-model="filters[field.key]" :placeholder="field.placeholder || '请输入条件'" />
          </label>
        </div>
        <footer v-if="mode !== 'products'" class="filter-actions">
          <doudian-batch-shop-scope v-model="shops" :shops="shopOptions" :disabled="confirming" />
          <div class="filter-buttons">
            <button v-if="mode === 'range'" class="more-search" @click="more = !more">
              {{ more ? '收起更多搜索 ▴' : '更多搜索 ▾' }}
            </button>
            <button :disabled="loading || confirming" @click="loadPreview(1)">查询</button>
            <button class="plain" @click="resetFilters">重置</button>
          </div>
        </footer>
        <p v-if="mode === 'range'" class="hint">
          不同条件同时满足才命中；一个 SKU 满足已设置的 SKU
          条件即可命中商品。条件筛选有些无法直接查询结果，通过数据命中来判断是否符合。
        </p>
      </section>
      <section class="panel methods">
        <h3><i></i>修改内容</h3>
        <div class="method-body">
          <div class="rule-row">
            <span>在售商品处理：</span>
            <el-radio-group v-model="allowOnsale" :disabled="confirming">
              <el-radio :label="false">遇到在售商品不删除</el-radio>
              <el-radio :label="true">遇到在售商品也删除</el-radio>
            </el-radio-group>
            <p class="hint">执行时先同步最新详情，所有匹配条件命中后才删除，不自动下架。</p>
          </div>
        </div>
      </section>
      <section v-if="preview" class="panel preview">
        <header class="preview-summary">
          <span>
            候选商品
            <b>{{ preview.total }}</b>
            个，已排除 {{ excluded.length }} 个；本页预计命中 {{ preview.page_match_counts.matched }} / 未命中
            {{ preview.page_match_counts.unmatched }} / 待核验
            {{ preview.page_match_counts.unknown }}
          </span>
          <button class="plain" @click="preview = null">收起预览</button>
        </header>
        <div class="preview-details">
          <span>本地快照仅用于预估，执行时会读取最新详情并重新核验。</span>
          <span v-if="!previewCurrent" class="warning">条件已变化，请重新预览。</span>
          <span v-if="missingIds.length" class="warning">未找到的商品 ID：{{ missingIds.join('、') }}</span>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>店铺 / 商品</th>
                <th>抖店商品 ID</th>
                <th>本地状态</th>
                <th>预计结果</th>
                <th>说明</th>
                <th>排除</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in preview.list" :key="item.id">
                <td>
                  <div class="preview-product">
                    <el-image class="preview-product-image" :src="productImage(item)" fit="cover">
                      <span slot="error">暂无图片</span>
                    </el-image>
                    <div>
                      <small>{{ item.shop_name }}</small>
                      <p>{{ item.product_title }}</p>
                    </div>
                  </div>
                </td>
                <td>{{ item.platform_product_id }}</td>
                <td>{{ statusText(item.old_platform_status) }}</td>
                <td>
                  {{ resultNames[item.preview_result] || item.preview_result }}
                </td>
                <td>
                  {{ item.reason || item.match_reason || item.error_msg || '—' }}
                </td>
                <td>
                  <el-checkbox :value="excluded.includes(Number(item.id))" @change="exclude(item.id, $event)" />
                </td>
              </tr>
              <tr v-if="!preview.list.length">
                <td colspan="6">暂无候选商品</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="preview-pagination">
          <el-pagination
            small
            layout="prev, pager, next"
            :current-page="preview.page"
            :page-size="preview.limit"
            :total="preview.total"
            @current-change="loadPreview"
          />
        </footer>
      </section>
    </main>
    <footer class="actions">
      <button class="preview-button" :disabled="loading || confirming" @click="loadPreview(1)">
        {{ loading ? '加载中…' : '删除内容预览' }}
      </button>
      <button class="start" :disabled="loading || confirming || !previewCurrent || !preview.total" @click="createBatch">
        {{ confirming ? '处理中…' : '开始批量删除' }}
      </button>
      <label>
        <input v-model="scheduled" type="checkbox" :disabled="confirming" @change="toggleSchedule" />
        定时执行
      </label>
      <button v-if="scheduled" class="time" :disabled="confirming" @click="openSchedule">
        {{ scheduledAt.slice(0, 16) }}
      </button>
    </footer>
    <div v-if="scheduleVisible" class="schedule-mask">
      <section class="schedule-modal" role="dialog" aria-modal="true" aria-label="定时执行">
        <header>
          <strong>定时执行</strong>
          <button aria-label="关闭" @click="closeSchedule">×</button>
        </header>
        <main>
          <el-date-picker
            v-model="scheduleDraft"
            type="datetime"
            format="yyyy-MM-dd HH:mm"
            value-format="yyyy-MM-dd HH:mm"
            :clearable="false"
            placeholder="请选择执行时间"
          />
        </main>
        <footer>
          <button class="plain" @click="closeSchedule">取消</button>
          <button @click="confirmSchedule">确定</button>
        </footer>
      </section>
    </div>
    <product-selector-dialog
      @page-size-change="changeSelectorPageSize"
      :visible="selectorVisible"
      :active-tab="selectorTab"
      :products="selectorProducts"
      :selected-ids="draftSelected.map(productId)"
      :loading="selectorLoading"
      :page="selectorPage"
      :page-size="selectorPageSize"
      :page-sizes="[20, 50, 100, 500, 1000]"
      :total="selectorTab === 'selected' ? draftSelected.length : selectorTotal"
      :total-pages="selectorPages"
      :jump-page="selectorJump"
      :page-fully-selected="selectorRows.length > 0 && selectorRows.every(isSelected)"
      :product-id="productId"
      :product-key="productId"
      :product-title="productTitle"
      :product-image="productImage"
      :product-price="productPrice"
      @tab-change="changeSelectorTab"
      @close="selectorVisible = false"
      @toggle-product="toggleProduct"
      @toggle-page="togglePage"
      @page-change="changeSelectorPage"
      @jump-input="selectorJump = $event"
      @jump="changeSelectorPage(Number(selectorJump))"
      @confirm="confirmSelected"
    >
      <template #filters>
        <doudian-product-selector-filters
          :value="selectorFilters"
          :shop-id="selectorShopId"
          :shops="shopOptions"
          :categories="categories"
          @shop-change="changeSelectorShop"
          @query="loadSelector(1)"
        />
      </template>
    </product-selector-dialog>
  </section>
</template>

<script>
import DoudianBatchShopScope from '@/components/DoudianBatchShopScope'
import {
  previewDoudianProductDeleteBatch,
  createDoudianProductDeleteBatch,
  getDoudianCategories,
  getAuthSuccessShopList,
  getProductSelectorProducts
} from '@/api/main/doudianBatchEdit'
import DoudianCategoryCascader from '@/components/DoudianCategoryCascader'
import ProductSelectorDialog from '@/components/ProductSelectorDialog/ProductSelectorDialog'
import DoudianProductSelectorFilters from './DoudianProductSelectorFilters.vue'
import { fields, pairedFields, defaults, buildBatchProductFilter } from './batchModifyFilters'

export default {
  name: 'BatchModifyDelete',
  components: {
    DoudianBatchShopScope,
    DoudianCategoryCascader,
    DoudianProductSelectorFilters,
    ProductSelectorDialog
  },
  data: () => ({
    tabs: [
      { value: 'range', label: '按范围修改' },
      { value: 'products', label: '按商品修改' },
      { value: 'product_ids', label: '按商品 ID 修改' }
    ],
    mode: 'range',
    fields,
    filters: defaults(),
    more: false,
    shops: [],
    shopOptions: [],
    categories: [],
    selected: [],
    excluded: [],
    idsText: '',
    allowOnsale: false,
    scheduled: false,
    scheduledAt: '',
    scheduleDraft: '',
    scheduleVisible: false,
    preview: null,
    signature: '',
    loading: false,
    confirming: false,
    previewRequest: 0,
    resultNames: {
      ready: '本地预计执行',
      skipped: '预计跳过',
      unknown: '待执行时核验'
    },
    selectorVisible: false,
    selectorTab: 'all',
    selectorLoading: false,
    selectorRows: [],
    selectorPageSize: 20,
    selectorPage: 1,
    selectorTotal: 0,
    selectorJump: 1,
    selectorShopId: '',
    selectorFilters: {
      category_id: { level1: [], level2: [], level3: [] },
      platform_product_id: '',
      product_title: '',
      created_at: [],
      tab_status: ''
    },
    draftSelected: [],
    selectorRequest: 0
  }),
  computed: {
    shownFields() {
      return this.fields
        .filter((field) => !Object.values(pairedFields).includes(field.key) && (!field.more || this.more))
        .map((field) =>
          pairedFields[field.key]
            ? {
                ...field,
                matchKey: pairedFields[field.key],
                matchOptions: this.fields.find((item) => item.key === pairedFields[field.key]).options
              }
            : field
        )
    },
    selectorPages() {
      return Math.max(
        1,
        Math.ceil((this.selectorTab === 'selected' ? this.draftSelected.length : this.selectorTotal) / this.selectorPageSize)
      )
    },
    selectorProducts() {
      return this.selectorTab === 'selected'
        ? this.draftSelected.slice((this.selectorPage - 1) * this.selectorPageSize, this.selectorPage * this.selectorPageSize)
        : this.selectorRows
    },
    previewCurrent() {
      return Boolean(this.preview && this.signature === JSON.stringify(this.payload(false)))
    },
    missingIds() {
      return this.preview && Array.isArray(this.preview.missing_platform_product_ids)
        ? this.preview.missing_platform_product_ids
        : []
    }
  },
  watch: {
    shops: {
      deep: true,
      handler() {
        this.selected = []
        this.excluded = []
      }
    },
    mode() {
      this.excluded = []
      this.preview = null
    }
  },
  mounted() {
    this.loadOptions()
  },
  beforeDestroy() {
    this.previewRequest++
    this.selectorRequest++
  },
  methods: {
    changeSelectorPageSize(size) {
      this.selectorPageSize = Number(size)
      this.selectorPage = 1
      this.loadSelector(1)
    },
    dataOf(response) {
      if (!response || Number(response.code) !== 200) throw new Error(response?.message || '请求失败')
      return response.data
    },
    message(error) {
      return error?.response?.data?.message || error?.message || '请求失败'
    },
    async loadOptions() {
      try {
        const data = this.dataOf(await getAuthSuccessShopList())
        this.shopOptions = (Array.isArray(data) ? data : data.list || data.data || []).filter(
          (shop) => shop.selectable !== false
        )
      } catch (error) {
        this.$message.error(this.message(error))
      }
      try {
        const data = this.dataOf(await getDoudianCategories({ shop_type: 1 }))
        const map = (items) =>
          items.map((item) => ({
            ...item,
            category_id: item.id || item.category_id,
            category_name: item.name || item.category_name,
            children: map(item.children || [])
          }))
        this.categories = map(Array.isArray(data) ? data : data.list || data.data || [])
      } catch (error) {
        this.$message.error(this.message(error))
      }
    },
    split(text) {
      return String(text)
        .split(/[,，\s]+/)
        .filter(Boolean)
    },
    payload(schedule = true) {
      const filter = buildBatchProductFilter(
        this.filters,
        this.shops,
        this.excluded,
        this.mode,
        this.selected.map((item) => String(item.platform_product_id))
      )
      if (this.mode === 'product_ids') {
        delete filter.status
        delete filter.created_at
        delete filter.category_id
        filter.platform_product_id = [...new Set(this.split(this.idsText))]
      }
      const payload = {
        selection_mode: this.mode,
        product_filter: filter,
        rules: [{ type: 901, options: { allow_onsale: this.allowOnsale } }]
      }
      if (schedule && this.scheduled) payload.scheduled_at = this.scheduledAt
      return payload
    },
    validate() {
      const payload = this.payload(false)
      const ids = payload.product_filter.platform_product_id
      if (this.mode !== 'range' && (!ids || !ids.length)) throw new Error('请选择商品或输入抖店商品 ID')
      if (ids && (ids.length > 10000 || ids.some((id) => !/^[1-9][0-9]{0,199}$/.test(id))))
        throw new Error('商品 ID 必须是数字，最多10000个')
      return payload
    },
    async loadPreview(page = 1) {
      if (this.loading || this.confirming) return
      let payload
      try {
        payload = this.validate()
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const signature = JSON.stringify(payload)
      const request = ++this.previewRequest
      this.loading = true
      this.signature = ''
      try {
        const data = this.dataOf(
          await previewDoudianProductDeleteBatch({
            ...payload,
            page,
            limit: 20
          })
        )
        if (request !== this.previewRequest || this._isDestroyed) return
        if (!Array.isArray(data?.list) || !Number.isInteger(data.total) || !data.page_match_counts)
          throw new Error('预览响应格式错误')
        this.preview = data
        this.signature = signature
      } catch (error) {
        if (request === this.previewRequest && !this._isDestroyed) this.$message.error(this.message(error))
      } finally {
        if (request === this.previewRequest) this.loading = false
      }
    },
    exclude(id, value) {
      this.excluded = value
        ? [...new Set([...this.excluded, Number(id)])]
        : this.excluded.filter((item) => item !== Number(id))
      this.signature = ''
    },
    resetFilters() {
      this.filters = defaults()
      this.excluded = []
      this.signature = ''
    },
    statusText(status) {
      return (
        {
          0: '草稿',
          1: '审核中',
          2: '审核拒绝',
          3: '待上架',
          4: '在售',
          5: '仓库中'
        }[status] || '待核验'
      )
    },
    formatScheduleTime(date = new Date(Date.now() + 60000)) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}`
    },
    toggleSchedule() {
      if (!this.scheduled) {
        this.scheduledAt = ''
        this.closeSchedule()
      } else if (!this.scheduledAt) this.scheduledAt = this.formatScheduleTime() + ':00'
    },
    openSchedule() {
      this.scheduleDraft = this.scheduledAt.slice(0, 16) || this.formatScheduleTime()
      this.scheduleVisible = true
    },
    closeSchedule() {
      this.scheduleVisible = false
    },
    confirmSchedule() {
      const timestamp = new Date(this.scheduleDraft.replace(' ', 'T') + '+08:00').getTime()
      if (
        !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(this.scheduleDraft) ||
        !Number.isFinite(timestamp) ||
        timestamp <= Date.now()
      )
        return this.$message.warning('请选择未来的执行时间')
      this.scheduledAt = this.scheduleDraft + ':00'
      this.closeSchedule()
    },
    async createBatch() {
      if (this.loading || this.confirming) return
      let kept
      try {
        this.validate()
        if (!this.previewCurrent) throw new Error('请先按当前条件预览')
        kept = Number(this.preview.total)
        if (kept < 1 || kept > 10000) throw new Error('候选商品必须为1–10000件')
        if (this.scheduled && new Date(this.scheduledAt.replace(' ', 'T') + '+08:00').getTime() <= Date.now())
          throw new Error('请选择未来的执行时间')
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const payload = this.payload()
      const frozen = JSON.stringify(payload)
      this.confirming = true
      try {
        const confirmationMessage = `候选 ${this.preview.total} 件，排除 ${this.excluded.length} 件。${
          this.allowOnsale ? '在售商品也删除' : '在售商品不删除'
        }；${this.scheduled ? this.scheduledAt + ' 执行' : '立即执行'}。执行时重新核验，恢复能力以平台规则为准。`
        const confirmationOptions = {
          type: 'warning',
          confirmButtonText: '确认创建删除任务',
          cancelButtonText: '取消',
          distinguishCancelAndClose: true,
          autofocus: false
        }
        if (kept > 100) {
          const confirmationPhrase = `确认删除${kept}个商品`
          await this.$prompt(`${confirmationMessage}请输入“${confirmationPhrase}”以继续。`, '确认删除任务', {
            ...confirmationOptions,
            inputPlaceholder: confirmationPhrase,
            inputValidator: (value) => value === confirmationPhrase,
            inputErrorMessage: `请输入“${confirmationPhrase}”后才能继续`
          })
        } else {
          await this.$confirm(confirmationMessage, '确认删除任务', confirmationOptions)
        }
        if (!this.previewCurrent || frozen !== JSON.stringify(this.payload()))
          throw new Error('条件已变化，请重新预览并确认')
        this.dataOf(await createDoudianProductDeleteBatch(payload))
        if (!this._isDestroyed) {
          this.$message.success('任务已创建，请查看批量修改记录')
          this.$emit('created')
        }
      } catch (error) {
        if (error !== 'cancel' && error !== 'close' && !this._isDestroyed) this.$message.error(this.message(error))
      } finally {
        this.confirming = false
      }
    },
    productId(item) {
      return Number(item.distribute_product_id || item.id)
    },
    productTitle(item) {
      return item.product_title || item.title || ''
    },
    productImage(item) {
      return item.main_image || item.img || ''
    },
    productPrice(item) {
      return item.product_price == null ? '价格待定' : '¥' + Number(item.product_price).toFixed(2)
    },
    isSelected(item) {
      return this.draftSelected.some((row) => this.productId(row) === this.productId(item))
    },
    toggleProduct(item) {
      if (
        !this.isSelected(item) &&
        this.draftSelected.length &&
        Number(this.draftSelected[0].shop_id) !== Number(item.shop_id)
      )
        return this.$message.warning('只能选择同一家店铺的商品')
      this.draftSelected = this.isSelected(item)
        ? this.draftSelected.filter((row) => this.productId(row) !== this.productId(item))
        : [...this.draftSelected, item]
    },
    togglePage() {
      const pageIds = this.selectorRows.map(this.productId)
      const all = this.selectorRows.every(this.isSelected)
      this.draftSelected = all
        ? this.draftSelected.filter((row) => !pageIds.includes(this.productId(row)))
        : [...this.draftSelected, ...this.selectorRows.filter((item) => !this.isSelected(item))]
    },
    changeSelectorTab(tab) {
      this.selectorTab = tab
      this.selectorPage = 1
      this.selectorJump = 1
    },
    changeSelectorPage(page) {
      const next = Math.min(this.selectorPages, Math.max(1, Number(page) || 1))
      if (this.selectorTab === 'all') return this.loadSelector(next)
      this.selectorPage = next
      this.selectorJump = next
    },
    changeSelectorShop(shopId) {
      this.selectorShopId = shopId
      this.loadSelector(1)
    },
    openSelector() {
      this.selectorShopId = this.selected[0]?.shop_id || ''
      this.draftSelected = [...this.selected]
      this.selectorTab = 'all'
      this.selectorVisible = true
      this.loadSelector(1)
    },
    async confirmSelected() {
      const shopId = Number(this.draftSelected[0]?.shop_id)
      if (!shopId || this.draftSelected.some((item) => Number(item.shop_id) !== shopId))
        return this.$message.warning('只能选择同一家店铺的商品')
      this.shops = [shopId]
      await this.$nextTick()
      this.selected = [...this.draftSelected]
      this.selectorVisible = false
      this.excluded = []
    },
    async loadSelector(page) {
      const request = ++this.selectorRequest
      this.selectorLoading = true
      try {
        const data = this.dataOf(
          await getProductSelectorProducts({
            page: Math.max(1, page),
            page_size: this.selectorPageSize,
            ...Object.fromEntries(
              Object.entries(this.selectorFilters).filter(
                ([, value]) => value != null && value !== '' && (!Array.isArray(value) || value.length)
              )
            ),
            ...(this.selectorShopId ? { shop_ids: [this.selectorShopId] } : {})
          })
        )
        if (request !== this.selectorRequest || this._isDestroyed) return
        if (!Array.isArray(data?.list)) throw new Error('商品响应格式错误')
        this.selectorRows = data.list
        this.selectorPage = Math.max(1, page)
        this.selectorJump = this.selectorPage
        this.selectorTotal = data.pagination?.total ?? data.total
      } catch (error) {
        if (request === this.selectorRequest && !this._isDestroyed) this.$message.error(this.message(error))
      } finally {
        if (request === this.selectorRequest) this.selectorLoading = false
      }
    }
  }
}
</script>

<style scoped src="./batch-modify.css"></style>
